import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
const _excluded = ["billId"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { api } from 'src/api';
import { types } from 'mobx-state-tree';
import { createAsync, createSilentAsync } from 'src/utils/asyncModel';
import { isHttpClientCancel } from 'src/core/services/api/httpClient';
import { uploadBillFile } from 'src/stores/billAutomation/uploadBillFile';
import { getCurrentCompanyId } from 'src/stores/billAutomation/currentCompanyId';
import { decideOriginalBillUse, ORIGINAL_BILL_USE } from 'src/stores/billAutomation/originalBillUse';

/**
 * Uploads a bill file and registers it: apply billing sending one file straight to the bill
 * bucket, for one property, cycle and utility (LB-5898).
 *
 * Silent, because the screen says what went wrong in its own words. The model raising a toast of
 * its own as well would mean two messages for one failed upload.
 */
const uploadBillAsync = createSilentAsync(body => {
  return async function uploadBill(options) {
    return uploadBillFile(body, options);
  };
});
const startBillAsync = createAsync(body => {
  return async function startBill(options) {
    const resp = await api.BillAutomationBills.startBill(body, options);
    return resp;
  };
});
const getBillAsync = createAsync(billId => {
  return async function getBill(options) {
    const resp = await api.BillAutomationBills.getBill(billId, options);
    return resp;
  };
});

/**
 * The same read, for the allocation table fetching the bill an allocation was made from.
 *
 * Its own model rather than a second caller of the one above, for two reasons. It is silent: a
 * bill deleted since it was allocated answers 404, and that is an allocation with no document
 * rather than something to interrupt the person about. And `run` cancels whatever its model already
 * has in flight, so sharing one with the details poll would have the two cancelling each other.
 */
const getAllocatedBillAsync = createSilentAsync(billId => {
  return async function getAllocatedBill(options) {
    const resp = await api.BillAutomationBills.getBill(billId, options);
    return resp;
  };
});

/**
 * The same read again, for apply billing asking whether the bill an allocation was made from is still on file before
 * anything else is attached to it (LB-5952). Its own model, so it neither cancels the lookup above nor is cancelled by
 * it. Silent: a 404 is the answer "no longer on file", read off the model's error.
 */
const getNamedBillOnFileAsync = createSilentAsync(billId => {
  return async function getNamedBillOnFile(options) {
    const resp = await api.BillAutomationBills.getBill(billId, options);
    return resp;
  };
});

/**
 * Recovers the bill uploaded for a billing cycle, property and utility — how apply billing finds the
 * document it was working on again after its modal closed. Resolves with a null `response` when
 * nothing has been uploaded for that cycle yet.
 */
const getBillForCycleAsync = createAsync(body => {
  return async function getBillForCycle(options) {
    const resp = await api.BillAutomationBills.getBillForCycle(body, options);
    return resp;
  };
});

/**
 * "Not for this table" (LB-6009): takes the bill the table brought in off it, keeping it for next month or leaving it on
 * the Bills page. Silent: the banner says how it went.
 */
const setBillNotForTableAsync = createSilentAsync(body => {
  return async function setBillNotForTable(options) {
    return api.BillAutomationBills.setBillNotForTable(body, options);
  };
});

/**
 * Bills for this table's property and utility that are scheduled for a later month (LB-6009): "A later bill is on
 * file". Silent: a note nobody asked for is never worth an error.
 */
const getScheduledBillsForTableAsync = createSilentAsync(body => {
  return async function getScheduledBillsForTable(options) {
    return api.BillAutomationBills.getScheduledBillsForTable(body, options);
  };
});

/**
 * "Use the original bill for this table" on a duplicate in apply billing (LB-6016): reads the original and where its
 * services stand, decides with decideOriginalBillUse, and attaches it to the table's property when it is waiting for one.
 * Resolves with `{ use, bill, message }`: `bill` is what to hold as this table's bill when the original was taken, and
 * `message` is why the attach did not happen, when it was refused or failed.
 *
 * Silent, and its own model: every outcome is an answer the banner words, and it must neither cancel the screen's other
 * reads nor be cancelled by them.
 */
const takeOriginalBillAsync = createSilentAsync(body => {
  return async function takeOriginalBill(options) {
    var _attached, _attached$message, _attached2, _await$read;
    const read = async () => {
      try {
        const resp = await api.BillAutomationBills.getBill(body.originalBillId, options);
        return resp !== null && resp !== void 0 && resp.isSuccessful ? resp.response : null;
      } catch (error) {
        // A click again cancels this one, which then answers nothing rather than "not on file".
        if (isHttpClientCancel(error)) throw error;
        // A 404 is "not on file any more"; anything else is unreachable, and either way there is nothing to take.
        return null;
      }
    };
    const original = await read();
    let allocations = null;
    const companyId = getCurrentCompanyId();
    if (original && companyId) {
      try {
        var _standing$response;
        const standing = await api.BillAutomationBills.getBillAllocations({
          companyId,
          billId: body.originalBillId
        }, options);
        allocations = standing !== null && standing !== void 0 && standing.isSuccessful ? (_standing$response = standing.response) !== null && _standing$response !== void 0 ? _standing$response : null : null;
      } catch (error) {
        if (isHttpClientCancel(error)) throw error;
        allocations = null;
      }
    }
    const use = decideOriginalBillUse({
      original,
      allocations,
      propertyId: body.propertyId,
      utilityId: body.utilityId
    });
    if (use === ORIGINAL_BILL_USE.hold) return {
      use,
      bill: original,
      message: null
    };
    if (use !== ORIGINAL_BILL_USE.attach) return {
      use,
      bill: null,
      message: null
    };

    // Attached with nothing taught: whether to trust its sender or keep its account number is the Bills page's
    // question, asked there with the bill in front of the person.
    let attached;
    try {
      attached = await api.BillAutomationBills.attachBill({
        billId: body.originalBillId,
        propertyId: body.propertyId,
        addAllowedSender: false,
        addAllowedSenderDomain: false,
        saveUtilityAccount: false
      }, options);
    } catch (error) {
      if (isHttpClientCancel(error)) throw error;
      return {
        use,
        bill: null,
        message: 'We couldn’t attach the original just now. Try again in a moment.'
      };
    }
    if (!((_attached = attached) !== null && _attached !== void 0 && _attached.isSuccessful)) return {
      use,
      bill: null,
      message: (_attached$message = (_attached2 = attached) === null || _attached2 === void 0 ? void 0 : _attached2.message) !== null && _attached$message !== void 0 ? _attached$message : null
    };

    // Read again for the status the attach left it in, which the screen then follows as it is read for this property.
    return {
      use,
      bill: (_await$read = await read()) !== null && _await$read !== void 0 ? _await$read : _objectSpread(_objectSpread({}, original), {}, {
        status: 'Extracted'
      }),
      message: null
    };
  };
});

/**
 * Pushes one uploaded bill to AppFolio AP. Resolves with the attempts the service created.
 * `overrideTotalAmount` is present only when a person edited the Bill total by hand, and
 * `invoiceNumber` only when they supplied one the extraction could not.
 */
const pushBillToAppfolioAsync = createAsync(body => {
  return async function pushBillToAppfolio(options) {
    const billId = body.billId,
      supplied = _objectWithoutProperties(body, _excluded);
    const resp = await api.BillAutomationBills.pushBillToAppfolio(billId, supplied, options);
    return resp;
  };
});

/** AppFolio AP push status for a batch of bills, so the dashboard can refresh several rows at once. */
const getAppfolioPushStatusAsync = createAsync(body => {
  return async function getAppfolioPushStatus(options) {
    const resp = await api.BillAutomationBills.getAppfolioPushStatus(body, options);
    return resp;
  };
});
const BillAutomationBillsApiStore = types.model('BillAutomationBillsApiStore', {
  startBillAsync,
  uploadBillAsync,
  getBillAsync,
  getAllocatedBillAsync,
  getNamedBillOnFileAsync,
  getBillForCycleAsync,
  setBillNotForTableAsync,
  getScheduledBillsForTableAsync,
  takeOriginalBillAsync,
  pushBillToAppfolioAsync,
  getAppfolioPushStatusAsync
});
export const billAutomationBillsApi = types.optional(BillAutomationBillsApiStore, {});